import React from 'react';
import Icon from '../../../components/AppIcon';
import StudentGradeRow from './StudentGradeRow';
import { StudentGradeRow as StudentGradeRowType, Assignment, GradeEntry } from '../types';

interface GradebookTableProps {
  studentRows: StudentGradeRowType[];
  assignments: Assignment[];
  onGradeChange: (studentId: string, assignmentId: string, grade: Partial<GradeEntry>) => void;
  onShowGradeDetails: (studentId: string, assignmentId: string) => void;
  onStudentClick: (studentId: string) => void;
  onAssignmentClick: (assignmentId: string) => void;
}

const GradebookTable = ({
  studentRows,
  assignments,
  onGradeChange,
  onShowGradeDetails,
  onStudentClick,
  onAssignmentClick,
}: GradebookTableProps) => {
  const getAssignmentTypeIcon = (type: string) => {
    switch (type) {
      case 'homework':
        return 'FileText';
      case 'quiz':
        return 'HelpCircle';
      case 'exam':
        return 'Award';
      case 'project':
        return 'Briefcase';
      case 'participation':
        return 'Users';
      default:
        return 'FileText';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="flex items-center border-b border-border bg-muted/50">
            <div
              className="sticky left-0 z-20 bg-muted/50 border-r border-border p-4"
              style={{ minWidth: '250px' }}
            >
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="text-sm font-semibold text-foreground">Elevi</span>
              </div>
            </div>

            <div className="flex items-center flex-1 overflow-x-auto">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex-shrink-0 p-4 cursor-pointer hover:bg-muted transition-colors duration-150"
                  style={{ minWidth: '120px' }}
                  onClick={() => onAssignmentClick(assignment.id)}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Icon
                      name={getAssignmentTypeIcon(assignment.type)}
                      size={18}
                      className="text-primary"
                    />
                    <div className="text-center">
                      <p className="text-xs font-medium text-foreground line-clamp-2">
                        {assignment.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {assignment.maxScore} pct
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(assignment.dueDate).toLocaleDateString('ro-RO')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="sticky right-0 z-20 bg-muted/50 border-l border-border p-4"
              style={{ minWidth: '120px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
                <span className="text-sm font-semibold text-foreground">Medie</span>
              </div>
            </div>
          </div>

          <div>
            {studentRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Icon name="Users" size={48} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground text-center">
                  Nu s-au găsit elevi conform filtrelor
                </p>
              </div>
            ) : (
              studentRows.map((studentRow) => (
                <StudentGradeRow
                  key={studentRow.student.id}
                  studentRow={studentRow}
                  assignments={assignments}
                  onGradeChange={onGradeChange}
                  onShowGradeDetails={onShowGradeDetails}
                  onStudentClick={onStudentClick}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GradebookTable;
